/*
 * Lé Soleil – Cart offer messages (quantity offers)
 * Shows a message + progress bar in the cart drawer and on the cart page:
 *   1 item  -> tells about the 2-item offer
 *   2 items -> tells about the 3-item offer
 *   3 items -> tells about the 4-item offer (+ free pouch)
 *   4+      -> tells the customer the Golden Pouch gift is theirs
 * The Golden Pouch and the free gift pouch are not counted.
 * On/off switch and texts: Online Store > Themes > Customize >
 * section "Lé Soleil – Cart offers".
 */
(() => {
  'use strict';

  const POUCH_HANDLE = 'the-golden-pouch';
  const POUCH_VARIANT_ID = 49784091967703;
  const GIFT_HANDLE = 'golden-pouch-gift-ls4g';
  const GIFT_VARIANT_ID = 67752288354519;
  const MAX_STEP = 4;

  const SETTINGS = (window.LeSoleilOffers && window.LeSoleilOffers.messages) || {};
  if (SETTINGS.enabled === false) return;

  const MESSAGES = {
1: 'Add <strong>3 more products</strong> to get a <strong>free Golden Pouch</strong> 🎁',
2: 'Add <strong>2 more products</strong> to get a <strong>free Golden Pouch</strong> 🎁',
3: 'Add <strong>1 more product</strong> to get a <strong>free Golden Pouch</strong> 🎁',
4: '🎁 <strong>You unlocked it!</strong> Your <strong>Golden Pouch is free</strong> with 4+ products!',};
  Object.keys(SETTINGS.texts || {}).forEach((key) => {
    if (SETTINGS.texts[key]) MESSAGES[key] = SETTINGS.texts[key];
  });

  const BAR_ID = 'ls-offer-bar';
  const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  const nativeFetch = window.fetch.bind(window);
  let lastCount = null;
  let timer = null;

  /* ---------- styles ---------- */
  const style = document.createElement('style');
  style.textContent = `
    .ls-offer-bar { margin: 0; padding: 14px 30px 16px; background: #fffaf2; border-bottom: 1px solid rgba(150,113,40,.18); color: #241b13; font-size: 14px; line-height: 1.45; text-align: center; }
    .ls-offer-bar[hidden] { display: none !important; }
    .ls-offer-bar__text strong { font-weight: 700; }
    .ls-offer-bar__text span { color: #967128; white-space: nowrap; }
    .ls-offer-bar__track { display: flex; gap: 6px; margin-top: 10px; }
    .ls-offer-bar__step { flex: 1; height: 6px; border-radius: 99px; background: rgba(150,113,40,.18); transition: background .3s ease; }
    .ls-offer-bar__step.is-done { background: #967128; }
    .ls-offer-bar__labels { display: flex; justify-content: space-between; margin-top: 5px; font-size: 11px; color: rgba(36,27,19,.55); }
    .ls-offer-bar--page { border: 1px solid rgba(150,113,40,.25); border-radius: 12px; margin: 0 0 20px; padding: 14px 20px 16px; }
  `;
  document.head.appendChild(style);

  /* ---------- markup ---------- */
  function buildBar(extraClass) {
    const bar = document.createElement('div');
    bar.id = BAR_ID + (extraClass ? '-page' : '');
    bar.className = 'ls-offer-bar' + (extraClass ? ' ' + extraClass : '');
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-live', 'polite');
    bar.hidden = true;
    bar.innerHTML = `
      <div class="ls-offer-bar__text"></div>
      <div class="ls-offer-bar__track">${'<span class="ls-offer-bar__step"></span>'.repeat(MAX_STEP)}</div>
      <div class="ls-offer-bar__labels"><span>1</span><span>2</span><span>3</span><span>4 + 🎁</span></div>
    `;
    return bar;
  }

  function renderInto(bar, count) {
    if (!bar) return;
    if (!count) { bar.hidden = true; return; }
    const step = Math.min(count, MAX_STEP);
    bar.hidden = false;
    bar.classList.toggle('is-complete', step >= MAX_STEP);
    bar.querySelector('.ls-offer-bar__text').innerHTML = MESSAGES[step];
    bar.querySelectorAll('.ls-offer-bar__step').forEach((el, i) => el.classList.toggle('is-done', i < step));
  }

  /* ---------- placement ---------- */
  function ensureDrawerBar() {
    const header = document.querySelector('#minicart_wrapper .minicart__header');
    if (!header) return null;
    let bar = document.getElementById(BAR_ID);
    if (!bar || bar.previousElementSibling !== header) {
      if (bar) bar.remove();
      bar = buildBar('');
      header.insertAdjacentElement('afterend', bar);
    }
    return bar;
  }

  function ensurePageBar() {
    if (!document.body.classList.contains('template-cart')) return null;
    let bar = document.getElementById(BAR_ID + '-page');
    if (bar && document.body.contains(bar)) return bar;
    const form = document.querySelector('#MainContent form[action$="/cart"], #MainContent form[action*="/cart"]');
    const anchor = form || document.querySelector('#MainContent');
    if (!anchor) return null;
    bar = buildBar('ls-offer-bar--page');
    if (form) form.insertAdjacentElement('beforebegin', bar);
    else anchor.prepend(bar);
    return bar;
  }

  function paint() {
    if (lastCount === null) return;
    renderInto(ensureDrawerBar(), lastCount);
    renderInto(ensurePageBar(), lastCount);
  }

  /* ---------- data ---------- */
  async function refresh() {
    try {
      const res = await nativeFetch(root + '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      lastCount = cart.items.reduce((n, item) => {
        const isPouch =
          item.handle === POUCH_HANDLE || Number(item.variant_id) === POUCH_VARIANT_ID ||
          item.handle === GIFT_HANDLE || Number(item.variant_id) === GIFT_VARIANT_ID;
        return n + (isPouch ? 0 : item.quantity);
      }, 0);
      paint();
    } catch (err) {
      console.error('[Cart offer bar]', err);
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 300);
  }

  function isCartMutation(url) {
    try {
      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(new URL(String(url || ''), location.origin).pathname);
    } catch (_) { return false; }
  }

  /* ---------- listen for cart changes ---------- */
  const prevFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    const p = prevFetch.apply(this, args);
    if (isCartMutation(url)) p.then((r) => { if (r.ok) schedule(); }).catch(() => {});
    return p;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__lsOfferCart = isCartMutation(url);
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__lsOfferCart) this.addEventListener('load', () => { if (this.status < 300) schedule(); });
    return xhrSend.apply(this, args);
  };

  document.addEventListener('cart:updated', schedule);

  // The theme re-renders the drawer after changes; put the bar back if it disappears.
  let paintQueued = false;
  new MutationObserver(() => {
    if (paintQueued) return;
    const drawerHeader = document.querySelector('#minicart_wrapper .minicart__header');
    const bar = document.getElementById(BAR_ID);
    if (drawerHeader && (!bar || bar.previousElementSibling !== drawerHeader)) {
      paintQueued = true;
      requestAnimationFrame(() => { paintQueued = false; paint(); });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
